<template>
  <section v-if="basics" class="basics pe-section">
    <section class="header">
      <div>
        <h2 class="name">
          <span
            v-editable="isPowerEdit"
            data-target-path="resume.basics.name"
            v-text="basics.name"
            @click="handleInfo"
          ></span>
          <SectionActions
            v-show="isPowerEdit"
            class="actions"
            section="basics"
            :actions="['edit']"
          />
        </h2>
      </div>
      <div class="contact">
        <span
          class="location contact-container"
          v-if="basics.location && nonEmptyLocationFields.length"
          :class="{
            'divider-address': nonEmptyLocationFields.length && basics.phone,
          }"
        >
          <template
            v-for="(field, index) in nonEmptyLocationFields"
            :key="field"
          >
            <span
              v-editable="isPowerEdit"
              :data-target-path="`resume.basics.location.${field}`"
              v-text="basics.location[field]"
              @click="handleInfo"
            ></span>
            <span v-if="index < nonEmptyLocationFields.length - 1">, </span>
          </template>
        </span>
        <span
          v-editable="isPowerEdit"
          data-target-path="resume.basics.email"
          class="email contact-container"
          :class="{ 'divider-url': basics.url && basics.email }"
          v-if="basics.email"
          v-text="basics.email"
          @click="handleInfo"
        ></span>
        <span
          v-editable="isPowerEdit"
          data-target-path="resume.basics.url"
          class="website contact-container"
          v-if="basics.url"
          v-text="basics.url"
          @click="handleInfo"
        ></span>
        <template
          v-for="(profile, index) in basics.profiles"
          :key="profile.url"
        >
          <span
            v-editable="isPowerEdit"
            :data-target-path="`resume.basics.profiles.${index}.url`"
            class="profile contact-container"
            v-text="profile.url"
            @click="handleInfo"
          ></span>
        </template>
        <span
          v-editable="isPowerEdit"
          data-target-path="resume.basics.phone"
          class="phone contact-container"
          v-if="basics.phone"
          v-text="basics.phone"
          @click="handleInfo"
        ></span>
      </div>
    </section>
  </section>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from 'vue'
import { Resume } from '@/types/resume/v1'
import { useStructuredResumeStore } from '@/store/pinia/structuredResume'
import SectionActions from './SectionActions.vue'
import { usePowerEditStore } from '@/store/pinia/powerEdit'
import { storeToRefs } from 'pinia'

export default defineComponent({
  name: 'Basics',
  components: {
    SectionActions,
  },
  props: {
    basics: {
      type: Object as PropType<Resume.Basics>,
      required: true,
    },
  },
  emits: ['showInfo'],
  setup(props, { emit }) {
    const store = useStructuredResumeStore()
    const powerEditStore = usePowerEditStore()
    const { closePopup, openRephrasePopup } = powerEditStore
    const { activeView } = storeToRefs(powerEditStore)
    const { isGeneratingSummary } = storeToRefs(store)

    const checkLocationNotEmpty = computed(() => {
      return (
        !!props.basics.location?.city ||
        !!props.basics.location?.region ||
        !!props.basics.location?.postalCode ||
        !!props.basics.location?.countryCode
      )
    })

    const nonEmptyLocationFields = computed(() => {
      const fields = ['city', 'region', 'postalCode', 'countryCode']
      return fields.filter(field => !!props.basics.location?.[field])
    })

    const handleInfo = () => {
      if (activeView.value === 'coverLetter') {
        emit('showInfo')
      }
    }

    return {
      isPowerEdit: store.isPowerEdit,
      isGeneratingSummary,
      checkLocationNotEmpty,
      closePopup,
      openRephrasePopup,
      nonEmptyLocationFields,
      handleInfo,
    }
  },
})
</script>

<style lang="postcss" scoped>
.basics {
  @apply text-base-em leading-[inherit] @container;
  .actions {
    @apply hidden absolute -right-6;
  }
  &:hover {
    .actions {
      @apply flex;
    }
  }
  .header {
    @apply flex flex-col items-start;
    @container (min-width: 32em) {
      @apply flex-row space-y-0 justify-between;
    }
  }
  .name {
    @apply text-2xl-em leading-none font-bold flex items-center mb-1-em relative;
  }
  .contact {
    @apply border-t border-black pt-2 text-sm w-full;
  }
}
</style>
